import { memo, useId } from "react";
import maiaFace from "../../../assets/maia.png";
import styles from "./issaInsights.module.scss";
import type { IssaAvatarState } from "./issaAnimatedAvatar.tsx";

/** Her head, from just above the hair down through the neck, in the portrait's
 *  own 256 x 256 pixels. It stays inside the white disc so the ring never moves. */
const HEAD = "62,52 70,40 84,28 96,20 112,13 128,11 144,13 160,19 172,28 184,40 196,56 206,70 "
  + "206,132 160,136 150,141 108,141 96,134 60,130 58,70";
/** The same outline cut off above the chin, painted as backdrop under the moving
 *  head so a tilt or nod shows the background instead of a second head. */
const BACKDROP = "62,52 70,40 84,28 96,20 112,13 128,11 144,13 160,19 172,28 184,40 196,56 206,70 "
  + "206,118 58,118 58,70";
/** The portrait faces left and the dock needs her facing the chat, so she is
 *  mirrored - all but the MAIKOZ lettering (x 123-158, y 164-191), which is laid
 *  back on unmirrored where the mirror put it. */
const MIRROR = "translate(256 0) scale(-1 1)";
const SKIN = "#f5a170";
const LASH = "#2b1a14";

/** The hat comes out for Christmas on its own - nobody has to remember it. */
function isChristmas(now: Date): boolean {
  return now.getMonth() === 11 && now.getDate() <= 26;
}

/**
 * Maia, animated like Issa: she breathes and glances aside while idle, leans in
 * while you type, tilts her head with her headset light pulsing while she thinks,
 * moves as she talks while the answer streams, nods, hops and winks when it
 * lands, and blinks throughout. There is no vector artwork of her, so the figure
 * is her portrait with the head cut out of it and the rest drawn on top.
 */
export const MaiaAnimatedAvatar = memo(function MaiaAnimatedAvatar({ state }: { state: IssaAvatarState }) {
  const id = useId().replace(/[^\w-]/g, "");
  const festive = isChristmas(new Date());

  return (
    <svg viewBox="0 0 256 256" data-state={state} className={styles.maiaFigure} role="img" aria-label="Maia">
      <defs>
        <filter id={`${id}-soft`} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
        <filter id={`${id}-lid`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.45" />
        </filter>
        <mask id={`${id}-head`} maskUnits="userSpaceOnUse" x="0" y="0" width="256" height="256">
          <polygon points={HEAD} fill="#fff" filter={`url(#${id}-soft)`} />
        </mask>
        <clipPath id={`${id}-logo`}>
          <rect x="98" y="164" width="35" height="27" />
        </clipPath>
      </defs>
      <g transform={MIRROR}>
        <image href={maiaFace} width="256" height="256" />
        <polygon points={BACKDROP} fill="#fefefe" filter={`url(#${id}-soft)`} />
      </g>
      <image href={maiaFace} x="-25" width="256" height="256" clipPath={`url(#${id}-logo)`} />
      <g transform={MIRROR}>
        <g className={styles.maiaHead}>
          <image href={maiaFace} width="256" height="256" mask={`url(#${id}-head)`} />
          <g className={styles.maiaLids} filter={`url(#${id}-lid)`}>
            <ellipse cx="100" cy="89.5" rx="8" ry="7" fill={SKIN} />
            <path d="M92.5 90 Q100 94.5 107.5 90" fill="none" stroke={LASH} strokeWidth="1.6" strokeLinecap="round" />
            <ellipse cx="132.5" cy="86" rx="9.5" ry="7.5" fill={SKIN} />
            <path d="M123.5 86.5 Q132.5 91 141.5 86.5" fill="none" stroke={LASH} strokeWidth="1.6" strokeLinecap="round" />
          </g>
          <g className={styles.maiaWink} filter={`url(#${id}-lid)`}>
            <ellipse cx="132.5" cy="86" rx="9.5" ry="7.5" fill={SKIN} />
            <path d="M123.5 86.5 Q132.5 91 141.5 86.5" fill="none" stroke={LASH} strokeWidth="1.6" strokeLinecap="round" />
          </g>
          <g className={styles.maiaLed}>
            <circle cx="135" cy="119" r="6.5" opacity="0.35" />
            <circle cx="135" cy="119" r="2.8" />
          </g>
          {festive && (
            <g className={styles.maiaHat}>
              <path d="M88 44Q104 12 150 14Q172 16 182 30L172 42Z" fill="#d62828" />
              <path d="M84 45Q128 27 174 40L176 50Q128 38 82 55Z" fill="#f8fafc" />
              <circle cx="184" cy="32" r="7" fill="#f8fafc" />
            </g>
          )}
        </g>
      </g>
    </svg>
  );
});
